"use client";

import { useEffect, useRef } from "react";

const STEPS = [
  {
    number: "01",
    title: "Ingest & Index",
    description: "Connect Google Drive, SharePoint, or upload files directly. Myndlayer chunks, embeds, and indexes every document into a tenant-isolated vector store — refreshing on your schedule.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
      </svg>
    ),
    detail: "Supports PDF, DOCX, HTML, earnings transcripts, SEC EDGAR feeds",
    color: "#6366F1",
  },
  {
    number: "02",
    title: "Retrieve Relevant Passages",
    description: "When you ask a question, the RAG pipeline performs semantic search across your document library, retrieving the top-k passages most relevant to your query — not keywords, meaning.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.35-4.35" />
        <path d="M11 8v6M8 11h6" />
      </svg>
    ),
    detail: "Hybrid BM25 + dense vector retrieval with reranking",
    color: "#F5A623",
  },
  {
    number: "03",
    title: "Synthesize & Cite",
    description: "Our AI synthesizes a grounded answer from the retrieved passages. Every claim is mapped to its source document, page number, and exact excerpt — no hallucinations, no guessing.",
    icon: (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M12 2L2 7l10 5 10-5-10-5z" />
        <path d="M2 17l10 5 10-5" />
        <path d="M2 12l10 5 10-5" />
      </svg>
    ),
    detail: "Grounded generation · inline citations",
    color: "#0FF4C6",
  },
];

export default function PipelineSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll(".reveal, .reveal-left, .reveal-right").forEach((el, i) => {
              setTimeout(() => el.classList.add("visible"), i * 120);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="section-pad relative overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0B0F1A 0%, #0D1526 100%)" }}
    >
      {/* Grid */}
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      {/* Glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(245,166,35,0.06), transparent)" }}
      />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16 reveal">
          <span className="section-label justify-center">The RAG Pipeline</span>
          <h2
            className="font-display text-4xl lg:text-5xl font-medium text-white leading-tight"
            style={{ fontFamily: "Fraunces, serif" }}
          >
            Three steps from question
            <br />
            <span className="gradient-text-amber">to verified answer</span>
          </h2>
        </div>

        {/* Steps */}
        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 relative">
          {/* Connector line (desktop only) */}
          <div
            className="hidden lg:block absolute top-14 left-1/3 right-1/3 h-px pointer-events-none"
            style={{ background: "linear-gradient(90deg, transparent, rgba(15,244,198,0.2), transparent)" }}
          />

          {STEPS?.map((step, i) => (
            <div
              key={step?.number}
              className="reveal bento-card p-7 lg:p-8 flex flex-col gap-5"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              {/* Step number + icon */}
              <div className="flex items-start justify-between">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: `${step?.color}15`, color: step?.color, border: `1px solid ${step?.color}25` }}
                >
                  {step?.icon}
                </div>
                <span
                  className="font-mono text-4xl font-bold leading-none"
                  style={{ color: `${step?.color}18` }}
                >
                  {step?.number}
                </span>
              </div>

              {/* Content */}
              <div>
                <h3
                  className="font-display text-xl font-semibold text-white mb-3"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  {step?.title}
                </h3>
                <p className="text-[#8892A4] text-sm leading-relaxed">
                  {step?.description}
                </p>
              </div>

              {/* Tech detail */}
              <div
                className="mt-auto pt-4 flex items-center gap-2"
                style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
              >
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: step?.color }} />
                <span className="text-[11px] font-mono text-[#4B5568]">{step?.detail}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Architecture callout */}
        <div
          className="mt-10 rounded-2xl p-6 flex flex-col md:flex-row items-center gap-6 reveal"
          style={{ background: "rgba(15,244,198,0.04)", border: "1px solid rgba(15,244,198,0.12)" }}
        >
          <div className="flex-shrink-0">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(15,244,198,0.1)", border: "1px solid rgba(15,244,198,0.2)" }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0FF4C6" strokeWidth="1.8">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
            </div>
          </div>
          <div className="flex-1 text-center md:text-left">
            <p className="text-sm font-semibold text-white mb-1">Multi-tenant isolation by design</p>
            <p className="text-xs text-[#8892A4] leading-relaxed">
              Every organization's documents live in a completely isolated vector namespace. Your data never trains shared models and is never accessible to other tenants.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}